import { isEmpty, isNil } from 'lodash';
import { CSSProperties, Fragment, useCallback, useEffect, useMemo } from 'react';
import { Button } from '@shared/components/Button';
import { Icon } from '@shared/components/Icon';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { EditField } from '@shared/scenes/entity/components/fields/EditField';
import { RelatedCardPrefills } from '@shared/scenes/entity/components/fields/RelatedEntityEditField';
import { CardEntityError } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { FieldRecord, UpdateField } from '@shared/scenes/entity/types';
import { getFieldError } from '@shared/scenes/entity/util/getFieldError';
import {
  isCombinedTagsComponent,
  isEntityBatchComponent,
  isLayoutComponent,
  isLayoutSection,
} from '@shared/scenes/workflows/utils';
import {
  hasLayoutContent,
  isLayoutContentItem,
  isLayoutImageItem,
  isLayoutTextItem,
  isLayoutVideoItem,
} from '@shared/scenes/workflows/viewTemplateLayoutItems';
import {
  LayoutImageItem,
  LayoutTextItem,
  LayoutVideoItem,
} from '@shared/scenes/workflows/views/view-components/layoutItems';
import { grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';
import { Storage, useLocalStorage } from '@shared/util/storage';
import { CardSummary } from './CardSummary';
import { EntityFormProps } from './EntityFormProps';
import styles from './EntityFormLayout.module.css';

export function EntityFormLayout({
  fields,
  updateField,
  components,
  errors,
  workflowTemplateId,
  entityId,
  isAuthenticated = true,
  layout,
}: EntityFormProps) {
  const layoutItems = useMemo(() => {
    const items = layout?.items ?? [];

    // Sanitize: filter out layout components not in components, and filter
    // section componentIds to only include existing components
    const sanitizedItems = items
      .map(item => {
        if (isLayoutSection(item)) {
          const filteredIds = (item.componentIds ?? []).filter(id =>
            components.some(c => c.id === id),
          );
          return { ...item, componentIds: filteredIds } as API.ViewTemplateLayoutSectionFragment;
        }
        return item;
      })
      .filter(item => {
        if (isLayoutComponent(item)) {
          return !!item.componentId && components.some(c => c.id === item.componentId);
        }
        if (isLayoutSection(item)) {
          return (item.componentIds?.length ?? 0) > 0;
        }
        // An instruction with nothing in it yet is dropped here rather than rendered as nothing,
        // so it does not count towards `isLastItem` and rob the section before it of its divider.
        if (isLayoutContentItem(item)) {
          return hasLayoutContent(item);
        }
        return true;
      });

    const layoutSections = sanitizedItems.filter(isLayoutSection);
    const layoutComponents = sanitizedItems.filter(isLayoutComponent);
    const otherComponents = components.filter(component => {
      const isInSection = isComponentInSections(component.id, layoutSections);
      const isStandalone = layoutComponents.some(item => item.componentId === component.id);
      const isEntityBatch = isEntityBatchComponent(component);
      const isCombinedTags = isCombinedTagsComponent(component);
      return !isInSection && !isStandalone && !isEntityBatch && !isCombinedTags;
    });
    const otherLayoutComponents = otherComponents.map(componentToLayoutComponent);
    const dedupedItems = sanitizedItems.filter(item => {
      if (isLayoutSection(item)) {
        return true;
      }
      if (isLayoutComponent(item)) {
        return !isComponentInSections(item.componentId ?? null, layoutSections);
      }
      // Text, image and video items. They name no component, so nothing above can have placed
      // them in a section and there is no duplicate to drop — they keep their position.
      return isLayoutContentItem(item);
    });

    return [...dedupedItems, ...otherLayoutComponents];
  }, [layout, components]);

  if (isNil(fields)) {
    return null;
  }

  return (
    <View className={[styles.formContainer, 'print-container']}>
      {isAuthenticated && !isNil(entityId) && (
        <CardSummary
          entityId={entityId}
          workflowTemplateId={workflowTemplateId}
          components={components}
        />
      )}
      <RelatedCardPrefills
        workflowTemplateId={workflowTemplateId}
        fields={fields}
        updateField={updateField}
        entityId={entityId}
      />
      {layoutItems.map((item, index, list) => {
        const previousItem = list[index - 1];
        const isLastItem = index === list.length - 1;
        return (
          <Fragment key={item.id}>
            <Spacer item={item} previousItem={previousItem} />
            <LayoutItem
              item={item}
              components={components}
              fields={fields}
              workflowTemplateId={workflowTemplateId}
              updateField={updateField}
              errors={errors}
              entityId={entityId}
              isAuthenticated={isAuthenticated}
              isLastItem={isLastItem}
            />
          </Fragment>
        );
      })}
    </View>
  );
}

function isComponentInSections(
  componentId: string | null,
  layoutSections: API.ViewTemplateLayoutSectionFragment[],
) {
  return layoutSections.some(section => section.componentIds?.includes(componentId as string));
}

function componentToLayoutComponent(
  component: API.AnyComponent,
): API.ViewTemplateLayoutComponentFragment {
  return {
    id: component.id,
    type: 'SINGLE_COMPONENT',
    componentId: component.id,
  };
}

/**
 * True for the items that carry their own bottom spacing and so need none added between them.
 *
 * A text, image or video item spaces itself to match a field, so `Spacer` treats it as one: no
 * gap between two of them or between one and a field, and the same divider a field earns where
 * a section follows.
 */
function spacesLikeAField(item: API.AnyViewTemplateLayoutItem) {
  return isLayoutComponent(item) || isLayoutContentItem(item);
}

function Spacer({
  item,
  previousItem,
}: {
  item: API.AnyViewTemplateLayoutItem;
  previousItem?: API.AnyViewTemplateLayoutItem;
}) {
  if (isNil(previousItem)) {
    return null;
  }
  if (spacesLikeAField(item) && spacesLikeAField(previousItem)) {
    return null;
  } else if (isLayoutSection(item) && isLayoutSection(previousItem)) {
    return null;
  } else if (spacesLikeAField(previousItem) && isLayoutSection(item)) {
    return <View className={styles.formSectionDivider}></View>;
  } else {
    return <View className={styles.sectionComponentSpacer} />;
  }
}

interface LayoutItemProps {
  item: API.AnyViewTemplateLayoutItem;
  components: API.AnyComponent[];
  fields: FieldRecord;
  workflowTemplateId: string;
  updateField: UpdateField;
  errors: CardEntityError[];
  entityId?: string;
  isAuthenticated: boolean;
  isLastItem: boolean;
}

function LayoutItem({
  item,
  components,
  fields,
  workflowTemplateId,
  updateField,
  errors,
  entityId,
  isAuthenticated,
  isLastItem,
}: LayoutItemProps) {
  if (isLayoutSection(item)) {
    return (
      <FormSection
        section={item}
        components={components}
        fields={fields}
        workflowTemplateId={workflowTemplateId}
        updateField={updateField}
        errors={errors}
        entityId={entityId}
        isAuthenticated={isAuthenticated}
        isLastItem={isLastItem}
      />
    );
  } else if (isLayoutComponent(item)) {
    const component = components.find(c => c.id === item.componentId);
    if (isNil(component) || isNil(item.componentId)) {
      return null;
    }
    return (
      <EditField
        field={fields[item.componentId]}
        fields={fields}
        workflowTemplateId={workflowTemplateId}
        component={component}
        updateField={updateField}
        error={getFieldError(errors, item.componentId)}
        entityId={entityId}
        isAuthenticated={isAuthenticated}
        style={cssStyles.standaloneFormComponent}
      />
    );
  }

  // Text, image and video items: content to read rather than a field to fill in. Indented to the
  // same gutter as a standalone field, with their own vertical spacing, so a form of both reads
  // as one column. Empty ones never reach here — they are dropped where the list is built.
  if (!isLayoutContentItem(item)) {
    return null;
  }

  return (
    <View style={cssStyles.standaloneContentItem}>
      {isLayoutTextItem(item) && <LayoutTextItem item={item} />}
      {isLayoutImageItem(item) && <LayoutImageItem item={item} />}
      {isLayoutVideoItem(item) && <LayoutVideoItem item={item} />}
    </View>
  );
}

interface FormSectionProps {
  section: API.ViewTemplateLayoutSectionFragment;
  components: API.AnyComponent[];
  fields: FieldRecord;
  workflowTemplateId: string;
  updateField: UpdateField;
  errors: CardEntityError[];
  entityId?: string;
  isAuthenticated: boolean;
  isLastItem: boolean;
}

function FormSection({
  section,
  components,
  fields,
  workflowTemplateId,
  updateField,
  errors,
  entityId,
  isAuthenticated,
  isLastItem,
}: FormSectionProps) {
  const collapsedKey = `collapsed-form-section-${workflowTemplateId}-${section.id}`;
  const [isCollapsed, setIsCollapsed] = useLocalStorage(collapsedKey, section.isCollapsed);
  const toggleCollapsed = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, [setIsCollapsed]);

  useEffect(() => {
    if (isCollapsed === !!section.isCollapsed) {
      Storage.remove(collapsedKey);
    }
  }, [collapsedKey, isCollapsed, section.isCollapsed]);

  const sectionComponents = useMemo(() => {
    const componentIds = section.componentIds ?? [];
    return componentIds
      .map(componentId => components.find(item => item.id === componentId))
      .filter(notNil);
  }, [components, section.componentIds]);

  if (isEmpty(sectionComponents)) {
    return null;
  }

  const isTwoColumn = section.columnCount === 2;
  const sectionClassName = [styles.formSection, isTwoColumn && styles.formSectionTwoColumn]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <View className={styles.formSectionHeader}>
        <Button variant="ghost" onClick={toggleCollapsed} className={styles.formSectionTitle}>
          <View className={[styles.formSectionTitleIcon, isCollapsed && styles.isCollapsed]}>
            <Icon name="arrow-point-down-heavy" gridSize={1.75} />
          </View>
          <Text size="subtitle" numberOfLines={1}>
            {section.title}
          </Text>
        </Button>
      </View>
      {!isCollapsed && (
        <View className={styles.formSection}>
          <View className={[sectionClassName, styles.formSectionContent]}>
            {sectionComponents.map(component => (
              <EditField
                key={component.id}
                field={fields[component.id]}
                fields={fields}
                workflowTemplateId={workflowTemplateId}
                component={component}
                updateField={updateField}
                error={getFieldError(errors, component.id)}
                entityId={entityId}
                isAuthenticated={isAuthenticated}
                style={cssStyles.formComponent}
              />
            ))}
          </View>
        </View>
      )}
      {isCollapsed && !isLastItem && <View className={styles.formSectionDivider}></View>}
    </>
  );
}

const cssStyles: Record<string, CSSProperties> = {
  formComponent: {
    paddingTop: grid(0.25),
    paddingBottom: grid(1.75),
  },
  // Merged ahead of time: EditField is memoized, so the style object it
  // receives must keep a stable identity across renders.
  standaloneFormComponent: {
    paddingTop: grid(0.25),
    paddingBottom: grid(1.75),
    marginLeft: grid(2),
    marginRight: grid(2),
    border: '1px solid transparent',
  },
  // A standalone field's gutter without its vertical padding: each content item carries its own
  // bottom spacing, so a text item and an image are not separated differently.
  standaloneContentItem: {
    marginLeft: grid(2),
    marginRight: grid(2),
  },
};
